import { useContext, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SingleSignOnService from '../../../shared/services/single-sign-on/single-sign-on.service';
import { TermsAndConditionsButton } from '../../presentation-components/signup-login/terms-and-conditions-button';
import { AuthLayout } from './auth/auth-layout';
import { AuthField } from './auth/auth-field';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

export const OrganisationDetailsPage = () => {
  const navigate = useNavigate();
  const singleSignOnService = new SingleSignOnService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  const branding = useAuthBranding();
  const [errorMessage, setErrorMessage] = useState<string>('');

  const organisationDetails = Yup.object().shape({
    organisationName: Yup.string().required('Organisation name is required'),
    termsAndConditions: Yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
  });

  const formik = useFormik({
    initialValues: {
      organisationName: '',
      termsAndConditions: false,
    },
    validationSchema: organisationDetails,
    onSubmit: (values) => {
      setErrorMessage('');
      return singleSignOnService
        .completeSingleSignOnSignUp(values)
        .then(() => {
          userDetailsContext.forceUpdateUserDetails().then((updatedUserDetails) => {
            navigate(updatedUserDetails?.isPartner ? '/partner-dashboard' : '/dashboard', updatedUserDetails?.isPartner ? undefined : { state: { justAuthenticated: true } });
          });
        })
        .catch((err) => {
          console.log(err);
          if (err.response?.data?.message) {
            setErrorMessage(err.response.data.message);
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
        });
    },
  });

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Set up your organisation
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          One last step to finish setting up your {branding.productName} workspace.
        </Typography>

        <AuthField
          id="organisationName"
          name="organisationName"
          label="Organisation or business name"
          placeholder="Acme Ltd"
          autoComplete="organization"
          icon={<BusinessOutlined />}
          value={formik.values.organisationName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.organisationName && Boolean(formik.errors.organisationName)}
          helperText={formik.touched.organisationName ? formik.errors.organisationName : undefined}
          focusRing={branding.focusRing}
        />

        <FormControlLabel
          sx={{ mt: '16px', alignItems: 'flex-start', ml: '-3px' }}
          control={
            <Checkbox
              id="termsAndConditions"
              name="termsAndConditions"
              checked={formik.values.termsAndConditions}
              onChange={formik.handleChange}
              disableRipple
              sx={{ py: 0, pr: 1 }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary, lineHeight: 1.5 }}>
              I accept the
              <TermsAndConditionsButton color={branding.secrInk} />
            </Typography>
          }
        />
        {formik.touched.termsAndConditions && formik.errors.termsAndConditions && (
          <FormHelperText error sx={{ ml: '2px', fontSize: '0.8125rem' }}>
            {formik.errors.termsAndConditions}
          </FormHelperText>
        )}

        {errorMessage && (
          <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
            {errorMessage}
          </FormHelperText>
        )}

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={formik.isSubmitting}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Finish setup'}
        </Button>
      </Box>
    </AuthLayout>
  );
};
